import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button, Card, Col, Dropdown, DropdownButton, Form, Row, Table } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { IPlatformFilteringParams } from '../dto/IPlatformFilteringParams';
import { useNavigate } from 'react-router';
import { PlatformItem } from '../../../model/platform/PlatformItem';
import { Platform } from '../../../model/platform/Platform';
import { PlatformService } from '../PlatformService';
import { useSelector } from 'react-redux';
import { ErrorToast } from '../../../utils/toasters';
import { getCorrectEnums } from '../../../utils/Functions';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

const PLATFORM_ITEM_COLUMNS: ITableColumn[] = [
    { label: 'Platform' },
    { label: 'Icon' },
    { label: 'Name' },
    { label: 'Type' },
    { label: 'Enabled' },
    { label: 'Long Term' },
    { label: 'Live' },
    { label: 'Likes' },
    { label: 'Dislikes' },
    { label: 'Comments' },
    { label: 'Shares' },
    { label: 'Action', className: 'text-end' },
];

export function PlatformItemList() {
    const navigate = useNavigate();

    const [change, setChange] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [platforms, setPlatform] = useState<PlatformItem[]>([]);
    const [mainPlatforms, setMainPlatforms] = useState<Platform[]>([]);
    const [state, setState] = useState<IPlatformFilteringParams>({
        name: '',
        page: 1,
        perPage: 15,
        platform: '',
        type: '',
    });
    const { platformType } = useSelector((state: any) => state.enums);

    useEffect(() => {
        let isCurrent = true;
        setDataLoading(true);

        PlatformService.getPlatformItems(state)
            .then(data => {
                if (!isCurrent) return;
                setPlatform(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => {
                if (isCurrent) ErrorToast(err);
            })
            .finally(() => {
                if (isCurrent) setDataLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [change, page]);

    useEffect(() => {
        PlatformService.getPlatforms()
            .then(data => setMainPlatforms(data.data))
            .catch(ErrorToast);
    }, []);

    function disablePlatform(id: number) {
        PlatformService.disablePlatformItem(id)
            .then(updatedPlatform => {
                const updatedPlatforms = platforms.map(platform => (platform.id === id ? updatedPlatform : platform));
                setPlatform(updatedPlatforms);
            })
            .catch(ErrorToast);
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setPage(updatePage);
    };

    function handleChange(evt: any) {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value,
        });
    }

    return (
        <>
            <NavLink
                className="btn btn-outline-primary"
                to={{
                    pathname: '/platform/item/form',
                }}
            >
                + Add platform item
            </NavLink>

            <Card className="mb-5 p-4 mt-3">
                <Row>
                    <Form.Group as={Col}>
                        <SingleSelectDropdown
                            placeholder="All platforms"
                            options={mainPlatforms.map(item => ({
                                value: item.name,
                                label: item.name,
                                image: item.path,
                            }))}
                            withImage
                            selected={toSelectValue(state.platform)}
                            onChange={value => handleChange({ target: { name: 'platform', value } })}
                        />
                    </Form.Group>

                    <Form.Group as={Col}>
                        <Form.Control
                            placeholder="Name"
                            className="autocomplete-input"
                            name="name"
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group as={Col}>
                        <SingleSelectDropdown
                            placeholder="All types"
                            options={(platformType ?? []).map((type: string) => ({
                                value: type,
                                label: getCorrectEnums(type),
                            }))}
                            selected={toSelectValue((state as any).type)}
                            onChange={value => handleChange({ target: { name: 'type', value } })}
                        />
                    </Form.Group>
                </Row>

                <div className="mt-4">
                    <Button
                        onClick={() => {
                            setState({ ...state, page: 1 });
                            setPage(1);
                            setChange(!change);
                        }}
                    >
                        Search
                    </Button>
                </div>
            </Card>

            <TableLayout
                pagination={{ page, totalPages }}
                listLength={platforms.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName="platform items"
                skeletonColumns={PLATFORM_ITEM_COLUMNS}
                skeletonRows={state.perPage}
            >
                <>
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {PLATFORM_ITEM_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {platforms.map(platform => (
                                <tr key={platform.id}>
                                    <td>{platform.platform}</td>
                                    <td>
                                        {platform.path ? (
                                            <img alt="" src={platform.path} style={{ width: 35, height: 35 }} />
                                        ) : (
                                            <img
                                                alt=""
                                                src="/images/placeholder.png"
                                                style={{ width: 35, height: 35 }}
                                            />
                                        )}
                                    </td>
                                    <td>{platform.name}</td>
                                    <td>{getCorrectEnums(platform.type)}</td>
                                    <td>
                                        {platform.isEnabled ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.isLongTerm ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.isLive ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.likes ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.dislikes ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.comments ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {platform.shares ? (
                                            <span className="word-break text-primary">
                                                <FaCheck />
                                            </span>
                                        ) : (
                                            <span className="word-break text-danger">
                                                <FaTimes />
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <DropdownButton align="start" title="Actions" id="dropdown-menu-align-right">
                                            <Dropdown.Item
                                                eventKey="1"
                                                onClick={() => navigate('/platform/item/form/' + platform?.id)}
                                            >
                                                Edit
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                onClick={() => disablePlatform(platform.id ?? 0)}
                                                eventKey="3"
                                            >
                                                {platform.isEnabled ? 'Disable' : 'Enable'}
                                            </Dropdown.Item>
                                        </DropdownButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {platforms.length < 15 && !!platforms.length && <div className="h-180px"></div>}
                </>
            </TableLayout>
        </>
    );
}
